import { useRef, useState } from "react";
import type { DateOption } from "flatpickr/dist/types/options";
import { defer } from "lodash";
import type { DateTimePickerProps } from "react-flatpickr";
import Flatpickr from "react-flatpickr";
import type DatePicker from "react-flatpickr";
import invariant from "tiny-invariant";
import { t } from "@/i18n-js/instance";
import { USAGE_SECTIONS, useUsageContext } from "@circle-react/providers";
import { Icon } from "@circle-react-shared/Icon";
import { Listbox } from "../Listbox";
import { DatePickerInput } from "./DatePickerInput";
import type { RangeSelectOption } from "./predefinedRangePickerOptions";
import {
  customRangeOption,
  defaultPredefinedOption,
  predefinedRangePickerOptions as defaultPredefinedRangePickerOptions,
} from "./predefinedRangePickerOptions";

interface DateRangePickerProps {
  defaultDateRange?: DateOption[];
  maxDate?: Date;
  minDate?: Date;
  onChange: (dates: [Date, Date], option: RangeSelectOption) => void;
  predefinedRange?: string | null;
  predefinedRangePickerOptions?: RangeSelectOption[];
}

export const DateRangePicker = ({
  defaultDateRange,
  onChange,
  minDate,
  maxDate,
  predefinedRange,
  predefinedRangePickerOptions = defaultPredefinedRangePickerOptions,
}: DateRangePickerProps) => {
  const { section } = useUsageContext();
  const flatPickrInstanceRef = useRef<DatePicker | null>(null);

  const getDefaultPredefinedRange = () => {
    if (predefinedRange) {
      const predefinedRangeOption = predefinedRangePickerOptions.find(
        option => option.value === predefinedRange,
      );
      if (
        predefinedRangeOption &&
        predefinedRangeOption.value !== customRangeOption.value
      ) {
        return predefinedRangeOption;
      }
    }
    if (defaultDateRange) {
      return customRangeOption;
    }
    return defaultPredefinedOption;
  };
  const [rangeSelectValue, setRangeSelectValue] = useState<RangeSelectOption>(
    getDefaultPredefinedRange,
  );

  const handleReady = () => {
    if (section === USAGE_SECTIONS.USER) return;

    defer(() => {
      const flatPickrInstance = flatPickrInstanceRef.current?.flatpickr;
      invariant(flatPickrInstance, "flatPickrInstance is null");
      /* Add a class on calendar container so that styles are customised accordingly. */
      flatPickrInstance.calendarContainer.classList.add("settings-app");
    });
  };

  const getDefaultDate = () => {
    if (rangeSelectValue && rangeSelectValue.value !== customRangeOption.value)
      return rangeSelectValue.onSelect();
    if (defaultDateRange) return defaultDateRange;
    return undefined;
  };

  const handleRangePickerChange: DateTimePickerProps["onChange"] = ([
    startDate,
    endDate,
  ]) => {
    if (!startDate || !endDate) return;
    onChange([startDate, endDate], customRangeOption);
    setRangeSelectValue(customRangeOption);
  };

  const handleRangeSelectChange = (option: RangeSelectOption) => {
    const [startDate, endDate] = option.onSelect();
    const flatPickrInstance = flatPickrInstanceRef.current?.flatpickr;
    invariant(flatPickrInstance, "flatPickrInstance is null");
    flatPickrInstance.setDate([startDate, endDate], false);
    onChange([startDate, endDate], option);
    setRangeSelectValue(option);
  };

  return (
    <div className="flex flex-col gap-y-2 sm:flex-row sm:items-center sm:gap-y-0">
      <Listbox
        value={rangeSelectValue}
        options={predefinedRangePickerOptions}
        onChange={handleRangeSelectChange}
        className="min-w-[12rem] rounded-s-md sm:rounded-r-none sm:rounded-s-md md:min-w-[13rem] lg:min-w-[14rem]"
      />
      <Flatpickr
        ref={flatPickrInstanceRef}
        options={{
          mode: "range",
          dateFormat: "M d, Y",
          defaultDate: getDefaultDate(),
          minDate,
          maxDate,
          onReady: handleReady,
        }}
        render={(props, ref) => (
          <button
            type="button"
            className="border-dark focus-within:bg-secondary flex w-full min-w-0 max-w-full cursor-pointer items-center gap-x-2 rounded-r-md border border-l-0 px-3 py-2 sm:min-w-0 sm:max-w-none sm:rounded-l-none"
            onClick={() => {
              flatPickrInstanceRef.current?.flatpickr?.open?.();
            }}
            aria-label={t("date_range.open_date_picker")}
          >
            <Icon
              type="16-calendar"
              size={16}
              className="text-default shrink-0"
            />
            <DatePickerInput ref={ref} {...props} />
          </button>
        )}
        onChange={handleRangePickerChange}
      />
    </div>
  );
};
